import { Component, OnInit } from '@angular/core';
import { LEVELS } from './levels';
import { AuthService } from '../../services/auth.service';
import { HttpClient } from '@angular/common/http';
import { of } from 'rxjs';

@Component({
  selector: 'app-sokoban',
  templateUrl: './sokoban.component.html',
  styleUrl: './sokoban.component.scss'
})

export class SokobanComponent implements OnInit {
    grid: string[][] = [];
    baseGrid: string[][] = [];

    playerRow = 0;
    playerCol = 0;

    currentLevel = 0;
    moves = 0;
    packets = 0;
    history: { grid: string[][], playerRow: number, playerCol: number }[] = [];
    levels = LEVELS;

    constructor(
        private httpClient: HttpClient,
        public authService: AuthService
    ) { };

  ngOnInit(): void {
    let savedIndex = 0;
    this.getGameProgress().subscribe(progress => {
        savedIndex = progress.level;
        this.loadLevel(savedIndex);
    });
    window.addEventListener('keydown', this.handleKey.bind(this));
  }

  loadLevel(index: number = 0) {
    this.packets = 0;
    this.currentLevel = index;
    this.saveGameProgress();

    const raw = this.levels[index];

    this.grid = raw.map((row, r) => row.split('').map((char, c) => {
      if (char === '@' || char === '+') {
        this.playerRow = r;
        this.playerCol = c;
        return ' ';
      } else if (char === '$' || char === '*') {
        this.packets++;
        return '$';
      } else {
        return ' ';
      }
    }));

    this.baseGrid = raw.map(row =>
      row.split('').map(char => {
        if (char === '#' || char === ' ') return char;
        if (char === '.') return '.';
        if (char === '+' || char === '*') return '.';
        return ' ';
      })
    );

    this.history = [];
  }

  nextLevel() {
    const next = this.currentLevel + 1;
    if (next < this.levels.length) {
      this.loadLevel(next);
    } else {
      alert('🎉 You completed all levels!');
    }
  }

  saveState() {
    this.history.push({
      grid: this.grid.map(row => [...row]),
      playerRow: this.playerRow,
      playerCol: this.playerCol
    });
  }

  undo() {
    const prev = this.history.pop();
    if (prev) {
      this.grid = prev.grid.map(row => [...row]);
      this.playerRow = prev.playerRow;
      this.playerCol = prev.playerCol;
    }
  }

  resetLevel() {
    this.loadLevel(this.currentLevel);
  }

  resetProgress() {
    localStorage.removeItem('sokoban-current-level');
    this.loadLevel(0);
  }

  handleKey(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowUp': this.move(-1, 0); break;
      case 'ArrowDown': this.move(1, 0); break;
      case 'ArrowLeft': this.move(0, -1); break;
      case 'ArrowRight': this.move(0, 1); break;
      case 'z': this.undo(); break;
      case 'r': this.resetLevel(); break;
    }
  }

  move(dRow: number, dCol: number) {
    const r = this.playerRow;
    const c = this.playerCol;
    const nr = r + dRow;
    const nc = c + dCol;
    const nnr = nr + dRow;
    const nnc = nc + dCol;

    const dest = this.grid[nr][nc];
    const base = this.baseGrid[nr][nc];

    // Wall block
    if (base === '#') return;

    // Pushing box
    if (dest === '$') {
      const nextBase = this.baseGrid[nnr][nnc];
      const nextDest = this.grid[nnr][nnc];
      if (nextBase === '#' || nextDest === '$') return; // Blocked

      this.grid[nnr][nnc] = '$'; // Move box
      this.grid[nr][nc] = ' ';   // Clear old box
    }

    // Move player
    this.playerRow = nr;
    this.playerCol = nc;
    this.moves++;
    this.saveState();

    if (this.checkWin()) {
      setTimeout(() => {
        alert('🎉 You win!');
        this.nextLevel(); // Optional: advance to next level automatically
      }, 100); // Delay slightly to allow DOM/render updates
    }
  }

  isPlayer(r: number, c: number): boolean {
    return r === this.playerRow && c === this.playerCol;
  }

  getChar(r: number, c: number): string {
    const obj = this.isPlayer(r, c) ? '@' : this.grid[r][c];
    const base = this.baseGrid[r][c];

    if (obj === '$' && base === '.') return '📦'; // Box on goal
    if (obj === '@' && base === '.') return '🙂'; // Player on goal
    if (obj === '$') return '📦';
    if (obj === '@') return '🙂';
    if (base === '.') return '⚪'; // Goal
    if (base === '#') return '🧱';
    return ' ';
  }

  getClasses(r: number, c: number): string[] {
    const classes: string[] = [];
    const base = this.baseGrid[r][c];
    const obj = this.isPlayer(r, c) ? '@' : this.grid[r][c];

    if (base === '#') classes.push('wall');
    if (base === '.') classes.push('goal');
    if (obj === '$') classes.push('box');
    if (this.isPlayer(r, c)) classes.push('player');

    return classes;
  }

  checkWin(): boolean {
    for (let r = 0; r < this.grid.length; r++) {
      for (let c = 0; c < this.grid[r].length; c++) {
        if (this.baseGrid[r][c] === '.' && this.grid[r][c] !== '$') {
          return false;
        }
      }
    }
    return true;
  }

    getGameProgress() {
        if (this.authService.loggedIn())
        {
            let token = this.authService.getToken();
            return this.httpClient.get<{ level: number, data: string | null }>(
                `https://ng.renner.se/games.api.php?action=get_progress`,
                {
                    params: {
                        game_id: 1, 
                    },
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
        }
        else
        {
            return of({ level: parseInt(localStorage.getItem('sokoban-current-level')), data: null });
        }
    }

    saveGameProgress()
    {
        if (this.authService.loggedIn())
        {
            let token = this.authService.getToken();
            return this.httpClient.post(
                `https://ng.renner.se/games.api.php?action=save_progress`,
                {
                    game_id: 1,
                    level: this.currentLevel
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            ).subscribe({
                next: (response) => {
                  console.log('Progress saved successfully:', response);
                },
                error: (err) => {
                  console.error('Failed to save progress:', err);
                }
              });
        }
        else
        {
            localStorage.setItem('sokoban-current-level', this.currentLevel.toString()); // Save to localStorage            
        }
    }
}
